// src/hooks/useSiteVersion.ts -- "New Site Version" detection (2026-11-04).
//
// Every build bakes a unique id into the bundle (__BUILD_ID__, see
// vite.config.ts) and emits the same id to /version.json next to index.html.
// This hook fetches /version.json (never cached) on mount, whenever the tab
// regains focus / becomes visible, and every 10 minutes while open. When the
// deployed id differs from the loaded one, `outdated` flips true and the Home
// page shows the "New Site Version — Press to Reload" button.
//
// Dev server: /version.json is not emitted, so the fetch 404s and the hook
// stays quiet.

import { useCallback, useEffect, useState } from "react";

export const LOADED_BUILD_ID: string = typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev";

const POLL_MS = 10 * 60 * 1000;

interface VersionFile {
  build?: string;
}

export async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const ct = res.headers.get("content-type") ?? "";
    if (!ct.includes("json")) return null; // SPA fallback HTML, not the file
    const body = (await res.json()) as VersionFile;
    return typeof body.build === "string" && body.build ? body.build : null;
  } catch {
    return null;
  }
}

export function useSiteVersion(): { outdated: boolean; deployed: string | null; reload: () => void; check: () => void } {
  const [deployed, setDeployed] = useState<string | null>(null);

  const check = useCallback(() => {
    void fetchDeployedBuildId().then((id) => {
      if (id) setDeployed(id);
    });
  }, []);

  useEffect(() => {
    check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(check, POLL_MS);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [check]);

  const reload = useCallback(() => {
    // Cache-bust the document itself; the hashed /assets/* files are immutable
    // so a fresh index.html is all that is needed to pick up the new bundle.
    const url = new URL(window.location.href);
    url.searchParams.set("v", String(Date.now()));
    window.location.replace(url.toString());
  }, []);

  const outdated = deployed !== null && LOADED_BUILD_ID !== "dev" && deployed !== LOADED_BUILD_ID;
  return { outdated, deployed, reload, check };
}
